/**
 * Unified universe renderer with 3 levels of detail (LOD).
 *
 * LOD 1 (scale < 0.35):  Supercluster overview — vibrant nebulae, cluster labels, nuclei
 * LOD 2 (0.35 – 0.9):   Mid detail — nodes visible, selective artist labels
 * LOD 3 (scale >= 0.9):  Full galaxy detail — links, full nodes, indicators, labels
 */

import { GALAXY_COLORS } from '../utils/constants';

// ─── Deterministic random ──────────────────────────────────────────────
function seededRandom(seed) {
  const h = Math.sin(seed) * 43758.5453;
  return h - Math.floor(h);
}

// ─── LOD interpolation helpers ─────────────────────────────────────────
function clamp01(v) { return Math.max(0, Math.min(1, v)); }

function getLODFactors(scale) {
  const labelFactor = clamp01((scale - 0.35) / 0.1);
  const detailFactor = clamp01((scale - 0.9) / 0.2);
  const nodeFactor = clamp01((scale - 0.25) / 0.15);
  const hazeFactor = 1 - clamp01((scale - 0.6) / 0.4);
  // Extra-bright boost for the farthest-out overview (< 0.35)
  const overviewBoost = 1 - clamp01((scale - 0.15) / 0.25);

  // Supercluster LOD: visible when zoomed far out, fades as you zoom into clusters
  const superclusterFactor = 1 - clamp01((scale - 0.08) / 0.12);
  // Cluster-level haze fades in as supercluster fades out
  const clusterHazeFade = clamp01((scale - 0.06) / 0.1);

  // Genre tags: fade in as clusters become distinct, fade out as artist labels appear
  const genreTagFactor = clamp01((scale - 0.10) / 0.08) * (1 - clamp01((scale - 0.30) / 0.10));

  return { labelFactor, detailFactor, nodeFactor, hazeFactor, overviewBoost, superclusterFactor, clusterHazeFade, genreTagFactor };
}

// ─── Detect mobile for adaptive framerate ──────────────────────────────
const IS_MOBILE = typeof navigator !== 'undefined' &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
const TARGET_FPS = IS_MOBILE ? 30 : 60;
const FRAME_BUDGET = 1000 / TARGET_FPS;
const BIG_BANG_DURATION = 2800;

// ─── Starfield (screen-space tiling) ───────────────────────────────────
// Stars are placed on a fixed grid of cells that tiles infinitely.
// Each cell has a deterministic set of stars derived from the cell coords,
// so pan/zoom always shows stars edge-to-edge with no visible rectangle.
const STAR_CELL_SIZE = 120;  // screen-px per tile cell
const STARS_PER_CELL = 3;    // stars in each cell

// ─── Cluster haze cache ────────────────────────────────────────────────
function buildClusterHazes(clusterMetas) {
  return clusterMetas.map((cm) => {
    const seed = cm.cx * 1000 + cm.cy * 7 + cm.index * 333;
    const dots = [];
    // Denser particle field for richer nebulae
    const dotCount = 60 + cm.memberCount * 12 + (cm.recCount || 0) * 5;
    const baseR = cm.visualRadius * 1.3;

    for (let i = 0; i < dotCount; i++) {
      const r1 = seededRandom(seed + i * 12.9898);
      const r2 = seededRandom(seed + i * 78.233 + 1.0);
      const r3 = seededRandom(seed + i * 45.164 + 2.0);
      const r4 = seededRandom(seed + i * 93.989 + 3.0);

      const angle = r1 * Math.PI * 2;
      // Concentrate more particles toward center for galaxy-like density falloff
      const radialT = r2 * r2 * r3;
      const dist = radialT * baseR;

      dots.push({
        x: cm.cx + dist * Math.cos(angle),
        y: cm.cy + dist * Math.sin(angle),
        size: (0.5 + r4 * 2.5) * Math.max(0.3, 1 - (dist / baseR) * 0.4),
        baseBrightness: Math.max(0.2, 1 - (dist / baseR) * 0.55),
        phase: r1 * Math.PI * 2,
        speed: 0.0008 + r3 * 0.002,
        h: cm.color.h + (r4 - 0.5) * 15, // slight hue variation
        s: cm.color.s,
        l: cm.color.l,
      });
    }

    // BCGs (Brightest Cluster Galaxies) — fewer, smaller bright spots
    const bcgCount = 3 + Math.floor(cm.memberCount * 0.4);
    for (let i = 0; i < bcgCount; i++) {
      const r1 = seededRandom(seed + i * 33.33 + 100);
      const r2 = seededRandom(seed + i * 77.77 + 200);
      const r3 = seededRandom(seed + i * 55.55 + 300);
      const angle = r1 * Math.PI * 2;
      const dist = r2 * r2 * baseR * 0.5;

      dots.push({
        x: cm.cx + dist * Math.cos(angle),
        y: cm.cy + dist * Math.sin(angle),
        size: 2 + r1 * 2.5,
        baseBrightness: 0.7 + r3 * 0.2,
        phase: r1 * Math.PI * 2,
        speed: 0.0005 + r2 * 0.001,
        h: cm.color.h, s: cm.color.s, l: cm.color.l,
        isBCG: true,
        glowSize: 6 + r1 * 8,
      });
    }

    return { dots, cm, baseR };
  });
}

// ─── Cosmic filaments between clusters ──────────────────────────────────
function buildFilaments(clusterMetas) {
  const filaments = [];
  for (let i = 0; i < clusterMetas.length; i++) {
    for (let j = i + 1; j < clusterMetas.length; j++) {
      const ci = clusterMetas[i];
      const cj = clusterMetas[j];
      const dx = cj.cx - ci.cx;
      const dy = cj.cy - ci.cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const ri = ci.visualRadius;
      const rj = cj.visualRadius;

      // Connect much more distant clusters for a web-like network
      const maxReach = (ri + rj) * 8;
      if (dist > maxReach) continue;

      const filDots = [];
      // Denser dots for richer filament streams
      const dotCount = Math.floor(dist / 5);
      const seed = ci.cx * 100 + cj.cy * 7 + i * 50;

      for (let k = 0; k < dotCount; k++) {
        const t = (k + 0.5) / dotCount;
        // Wider lateral spread for organic, fibrous appearance
        const noise = (seededRandom(seed + k * 12.9898) - 0.5) * 45;
        const noiseY = (seededRandom(seed + k * 45.164 + 5) - 0.5) * 20;

        filDots.push({
          x: ci.cx + dx * t + (-dy / dist) * noise,
          y: ci.cy + dy * t + (dx / dist) * noise + noiseY,
          size: 0.5 + seededRandom(seed + k * 78.233) * 1.5,
          t,
          h: ci.color.h + (cj.color.h - ci.color.h) * t,
          phase: seededRandom(seed + k * 93.989) * Math.PI * 2,
        });
      }

      const proximity = 1 - dist / maxReach;
      filaments.push({ dots: filDots, proximity });
    }
  }
  return filaments;
}

// ─── Neuron filaments between clusters within a supercluster ───────────
// Builds a dense mesh of fine lines radiating from cluster nodes, creating
// a neural-network web effect. Density comes from quantity of thin lines,
// not from wide strokes.
function buildNeuronFilaments(superclusterMetas, clusterMetas) {
  if (!superclusterMetas || superclusterMetas.length <= 1) return null;

  const clusterMap = new Map();
  for (const cm of clusterMetas) clusterMap.set(cm.index, cm);

  // lines: { x1,y1, x2,y2, h,s,l, alpha }  — lightweight for fast rendering
  // nodes: { x,y, h,s,l, size }  — glow points at cluster centers
  const lines = [];
  const nodes = [];

  for (const sc of superclusterMetas) {
    const ids = sc.clusterIds;
    if (ids.length < 2) continue;

    const members = ids.map((id) => clusterMap.get(id)).filter(Boolean);
    if (members.length < 2) continue;

    // --- Connect ALL pairs with slight curves ---
    for (let i = 0; i < members.length; i++) {
      for (let j = i + 1; j < members.length; j++) {
        const ci = members[i];
        const cj = members[j];
        const dx = cj.cx - ci.cx;
        const dy = cj.cy - ci.cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 1) continue;

        const maxReach = (ci.visualRadius + cj.visualRadius) * 8;
        const proximity = clamp01(1 - dist / maxReach);
        if (proximity < 0.05) continue;

        const seed = ci.cx * 31 + cj.cy * 17 + sc.id * 131;
        const px = -dy / dist;
        const py = dx / dist;

        // Main connection line with slight bend
        const bend = (seededRandom(seed) - 0.5) * dist * 0.15;
        const mx = (ci.cx + cj.cx) / 2 + px * bend;
        const my = (ci.cy + cj.cy) / 2 + py * bend;
        const h = (ci.color.h + cj.color.h) / 2;
        const s = Math.round((ci.color.s + cj.color.s) / 2);
        const l = Math.round((ci.color.l + cj.color.l) / 2);

        lines.push({ x1: ci.cx, y1: ci.cy, cx: mx, cy: my, x2: cj.cx, y2: cj.cy,
          h, s, l, alpha: 0.15 + proximity * 0.25 });

        // 2-5 parallel offset lines for each pair — builds density
        const paraCount = 2 + Math.floor(seededRandom(seed + 3) * 4);
        for (let p = 0; p < paraCount; p++) {
          const pSeed = seed + p * 77 + 200;
          const offset = (seededRandom(pSeed) - 0.5) * dist * 0.2;
          const bendP = (seededRandom(pSeed + 1) - 0.5) * dist * 0.25;
          lines.push({
            x1: ci.cx + px * offset * 0.3,
            y1: ci.cy + py * offset * 0.3,
            cx: mx + px * (offset + bendP),
            cy: my + py * (offset + bendP),
            x2: cj.cx + px * offset * 0.3,
            y2: cj.cy + py * offset * 0.3,
            h, s, l,
            alpha: (0.06 + proximity * 0.12) * (0.4 + seededRandom(pSeed + 2) * 0.6),
          });
        }
      }
    }

    // --- Radial fan lines from each cluster node ---
    // These create the starburst effect at each node visible in the reference
    for (const cm of members) {
      const fanCount = 8 + Math.floor(cm.memberCount * 1.5);
      const fanSeed = cm.cx * 99 + cm.cy * 33 + sc.id * 777;

      for (let f = 0; f < fanCount; f++) {
        const fSeed = fanSeed + f * 53;
        const angle = seededRandom(fSeed) * Math.PI * 2;
        const length = cm.visualRadius * (0.6 + seededRandom(fSeed + 1) * 2.5);
        const bendAngle = angle + (seededRandom(fSeed + 2) - 0.5) * 0.6;

        const endX = cm.cx + Math.cos(angle) * length;
        const endY = cm.cy + Math.sin(angle) * length;
        const ctrlX = cm.cx + Math.cos(bendAngle) * length * 0.55;
        const ctrlY = cm.cy + Math.sin(bendAngle) * length * 0.55;

        lines.push({
          x1: cm.cx, y1: cm.cy,
          cx: ctrlX, cy: ctrlY,
          x2: endX, y2: endY,
          h: cm.color.h + (seededRandom(fSeed + 3) - 0.5) * 20,
          s: cm.color.s,
          l: cm.color.l,
          alpha: 0.05 + seededRandom(fSeed + 4) * 0.15,
        });
      }

      // Node glow point
      nodes.push({
        x: cm.cx, y: cm.cy,
        h: cm.color.h, s: cm.color.s, l: cm.color.l,
        size: Math.max(4, cm.visualRadius * 0.12),
      });
    }
  }

  return { lines, nodes };
}

// ─── Supercluster haze cache ────────────────────────────────────────────
function buildSuperclusterHazes(superclusterMetas) {
  if (!superclusterMetas || superclusterMetas.length <= 1) return null;

  return superclusterMetas.map((sc) => {
    const seed = sc.cx * 777 + sc.cy * 13 + sc.id * 999;
    const dots = [];
    const dotCount = 40 + sc.totalMembers * 6 + sc.totalRecs * 2;
    const baseR = sc.visualRadius * 1.5;

    for (let i = 0; i < dotCount; i++) {
      const r1 = seededRandom(seed + i * 12.9898);
      const r2 = seededRandom(seed + i * 78.233 + 1.0);
      const r3 = seededRandom(seed + i * 45.164 + 2.0);
      const r4 = seededRandom(seed + i * 93.989 + 3.0);

      const angle = r1 * Math.PI * 2;
      const radialT = r2 * r2 * r3;
      const dist = radialT * baseR;

      dots.push({
        x: sc.cx + dist * Math.cos(angle),
        y: sc.cy + dist * Math.sin(angle),
        size: (0.8 + r4 * 3.5) * Math.max(0.3, 1 - (dist / baseR) * 0.4),
        baseBrightness: Math.max(0.15, 1 - (dist / baseR) * 0.5),
        phase: r1 * Math.PI * 2,
        speed: 0.0005 + r3 * 0.0015,
        h: sc.color.h + (r4 - 0.5) * 20,
        s: sc.color.s,
        l: sc.color.l,
      });
    }

    // Bright core spots
    const bcgCount = 3 + Math.floor(sc.totalMembers * 0.3);
    for (let i = 0; i < bcgCount; i++) {
      const r1 = seededRandom(seed + i * 33.33 + 500);
      const r2 = seededRandom(seed + i * 77.77 + 600);
      const r3 = seededRandom(seed + i * 55.55 + 700);
      const angle = r1 * Math.PI * 2;
      const dist = r2 * r2 * baseR * 0.4;

      dots.push({
        x: sc.cx + dist * Math.cos(angle),
        y: sc.cy + dist * Math.sin(angle),
        size: 4 + r1 * 6,
        baseBrightness: 0.7 + r3 * 0.3,
        phase: r1 * Math.PI * 2,
        speed: 0.0004 + r2 * 0.0008,
        h: sc.color.h, s: sc.color.s, l: sc.color.l,
        isBCG: true,
        glowSize: 14 + r1 * 16,
      });
    }

    return { dots, sc, baseR };
  });
}

// ─── Main renderer factory ─────────────────────────────────────────────

export function createUniverseRenderer(canvas, getState) {
  const ctx = canvas.getContext('2d');
  let frameId = null;
  const startTime = performance.now();
  let lastFrameTime = 0;
  let isDocumentVisible = true;

  let stars = null;
  let hazes = null;
  let filaments = null;
  let scHazes = null;
  let neuronFils = null;
  let lastMetasKey = null;
  let worldCenterX = 0;
  let worldCenterY = 0;

  // Label lookup cache — avoids O(n) .find() per label per frame
  let labelMap = null;
  let labelMapKey = null;

  function ensureCaches(clusterMetas, worldBounds, superclusterMetas) {
    const key = clusterMetas.map((c) => `${c.cx},${c.cy},${c.totalCount}`).join('|');
    if (key === lastMetasKey) return;
    lastMetasKey = key;
    stars = true; // stars are now procedurally tiled, no pre-generation needed
    hazes = buildClusterHazes(clusterMetas);
    filaments = buildFilaments(clusterMetas);
    scHazes = buildSuperclusterHazes(superclusterMetas);
    neuronFils = buildNeuronFilaments(superclusterMetas, clusterMetas);
    labelMap = null; // invalidate label cache
    worldCenterX = 0;
    worldCenterY = 0;
    for (const c of clusterMetas) { worldCenterX += c.cx; worldCenterY += c.cy; }
    if (clusterMetas.length > 0) {
      worldCenterX /= clusterMetas.length;
      worldCenterY /= clusterMetas.length;
    }
  }

  function ensureLabelMap(allNodes, clusterMetas) {
    const key = allNodes.length + '|' + clusterMetas.length;
    if (labelMap && labelMapKey === key) return;
    labelMapKey = key;
    labelMap = new Map();
    for (const node of allNodes) {
      if (node.x == null) continue;
      const k = node.name + '|' + node.clusterId;
      labelMap.set(k, node);
    }
  }

  function isVisible(wx, wy, radius, transform, vw, vh) {
    const sx = wx * transform.scale + transform.x;
    const sy = wy * transform.scale + transform.y;
    const sr = radius * transform.scale;
    return sx + sr > -50 && sx - sr < vw + 50 && sy + sr > -50 && sy - sr < vh + 50;
  }

  // Pause when tab is not visible
  function handleVisibilityChange() {
    isDocumentVisible = !document.hidden;
    if (isDocumentVisible && frameId === null) {
      lastFrameTime = performance.now();
      frameId = requestAnimationFrame(render);
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);

  function render() {
    // Frame pacing — skip if we're ahead of the target framerate
    const now = performance.now();
    if (now - lastFrameTime < FRAME_BUDGET - 1) {
      frameId = requestAnimationFrame(render);
      return;
    }
    lastFrameTime = now;

    // Pause rendering while tab is hidden
    if (!isDocumentVisible) {
      frameId = null;
      return;
    }

    const state = getState();
    if (!state || !state.clusterMetas || state.clusterMetas.length === 0) {
      frameId = requestAnimationFrame(render);
      return;
    }

    const { clusterMetas, allNodes, allLinks, transform, worldBounds,
            hoveredNode, selectedNode, favoriteNames, dislikeNames, discoveredNames,
            superclusterMetas } = state;
    const time = now - startTime;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;
    const scale = transform.scale;
    const lod = getLODFactors(scale);
    const hasSuperClusters = superclusterMetas && superclusterMetas.length > 1;

    // --- Big bang animation progress ---
    const bigBangStart = state.bigBangStart || 0;
    const bigBangT = bigBangStart ? clamp01((now - bigBangStart) / BIG_BANG_DURATION) : 1;
    const bigBangActive = bigBangT < 1;
    const bbExpand = bigBangActive ? 1 - Math.pow(1 - bigBangT, 2.5) : 1;
    const bbHazeReveal = bigBangActive ? clamp01((bigBangT - 0.15) / 0.6) : 1;

    ensureCaches(clusterMetas, worldBounds, superclusterMetas);

    // --- Gentle orbital drift for living-map feel (only for visible nodes) ---
    if (allNodes) {
      for (const node of allNodes) {
        if (node.homeX == null) continue;
        const effX = bigBangActive ? worldCenterX + (node.homeX - worldCenterX) * bbExpand : node.homeX;
        const effY = bigBangActive ? worldCenterY + (node.homeY - worldCenterY) * bbExpand : node.homeY;
        if (!isVisible(effX, effY, (node.driftRadius || 0) + (node.radius || 5) * 3, transform, w, h)) continue;
        const t = time * node.driftSpeed + node.driftPhase;
        if (bigBangActive) {
          const driftScale = clamp01(bigBangT * 2 - 1);
          node.x = effX + Math.sin(t) * node.driftRadius * driftScale;
          node.y = effY + Math.cos(t * 0.7 + 1.3) * node.driftRadius * driftScale;
        } else {
          node.x = node.homeX + Math.sin(t) * node.driftRadius;
          node.y = node.homeY + Math.cos(t * 0.7 + 1.3) * node.driftRadius;
        }
      }
    }

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Background gradient
    const maxDim = Math.max(w, h);
    const bg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, maxDim * 0.7);
    bg.addColorStop(0, GALAXY_COLORS.backgroundGradientInner);
    bg.addColorStop(1, GALAXY_COLORS.backgroundGradientOuter);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // --- Big bang singularity flash (screen space, subtle) ---
    if (bigBangActive && bigBangT < 0.45) {
      const flashT = bigBangT / 0.45;
      const flashAlpha = (1 - flashT * flashT) * 0.45;
      const flashR = maxDim * 0.35 * Math.pow(flashT, 0.35);
      if (flashR > 0) {
        const flash = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, flashR);
        const warmth = 1 - flashT;
        const cR = Math.round(255 * (0.7 + warmth * 0.3));
        const cG = Math.round(220 + warmth * 35);
        const cB = Math.round(180 + (1 - warmth) * 60);
        flash.addColorStop(0, `rgba(${cR}, ${cG}, ${Math.min(cB, 255)}, ${flashAlpha})`);
        flash.addColorStop(0.3, `rgba(200, 190, 220, ${flashAlpha * 0.3})`);
        flash.addColorStop(1, 'rgba(100, 120, 180, 0)');
        ctx.fillStyle = flash;
        ctx.fillRect(0, 0, w, h);
      }
    }

    // World-space rendering
    ctx.save();
    ctx.translate(transform.x, transform.y);
    ctx.scale(scale, scale);

    // --- Starfield (procedural tiling, edge-to-edge) ---
    if (stars) {
      ctx.restore(); // temporarily exit world-space transform
      const starReveal = bigBangActive ? clamp01(bigBangT * 2.5) : 1;

      // Figure out which world-space region is visible, then tile cells
      const cellW = STAR_CELL_SIZE / scale; // cell size in world units
      const worldLeft   = -transform.x / scale;
      const worldTop    = -transform.y / scale;
      const worldRight  = (w - transform.x) / scale;
      const worldBottom = (h - transform.y) / scale;

      const cellMinX = Math.floor(worldLeft / cellW) - 1;
      const cellMaxX = Math.floor(worldRight / cellW) + 1;
      const cellMinY = Math.floor(worldTop / cellW) - 1;
      const cellMaxY = Math.floor(worldBottom / cellW) + 1;

      for (let cy = cellMinY; cy <= cellMaxY; cy++) {
        for (let cx = cellMinX; cx <= cellMaxX; cx++) {
          for (let i = 0; i < STARS_PER_CELL; i++) {
            const seed = cx * 74856.317 + cy * 26791.913 + i * 41053.771;
            const rx = seededRandom(seed);
            const ry = seededRandom(seed + 12345.678);
            const ra = seededRandom(seed + 98765.432);
            const rp = seededRandom(seed + 55555.111);

            const wx = (cx + rx) * cellW;
            const wy = (cy + ry) * cellW;
            const sx = wx * scale + transform.x;
            const sy = wy * scale + transform.y;

            const baseAlpha = 0.15 + ra * 0.45;
            const twinkle = (baseAlpha + 0.1 * Math.sin(time * 0.001 + rp * 100)) * starReveal;
            if (twinkle <= 0) continue;

            ctx.beginPath();
            ctx.arc(sx, sy, 0.8, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(200, 210, 230, ${twinkle})`;
            ctx.fill();
          }
        }
      }

      // Re-enter world-space transform
      ctx.save();
      ctx.translate(transform.x, transform.y);
      ctx.scale(scale, scale);
    }

    // --- Supercluster hazes (visible at extreme zoom-out) ---
    if (scHazes && hasSuperClusters && lod.superclusterFactor > 0) {
      const scBrightness = lod.superclusterFactor * bbHazeReveal;

      for (const { dots, sc, baseR } of scHazes) {
        const hazeCx = bigBangActive ? worldCenterX + (sc.cx - worldCenterX) * bbExpand : sc.cx;
        const hazeCy = bigBangActive ? worldCenterY + (sc.cy - worldCenterY) * bbExpand : sc.cy;
        if (!isVisible(hazeCx, hazeCy, baseR * 2, transform, w, h)) continue;

        ctx.save();
        if (bigBangActive) ctx.translate(hazeCx - sc.cx, hazeCy - sc.cy);

        const breathe = 1 + 0.03 * Math.sin(time * 0.0003 + sc.cx * 0.005);
        const { h: ch, s: cs, l: cl } = sc.color;

        // Large diffuse outer halo
        const outerR = baseR * 1.8 * breathe;
        const outer = ctx.createRadialGradient(sc.cx, sc.cy, 0, sc.cx, sc.cy, outerR);
        const outerAlpha = 0.06 * scBrightness;
        outer.addColorStop(0, `hsla(${ch}, ${cs}%, ${cl}%, ${Math.min(outerAlpha * 1.2, 0.18)})`);
        outer.addColorStop(0.3, `hsla(${ch}, ${cs}%, ${cl}%, ${Math.min(outerAlpha * 0.5, 0.09)})`);
        outer.addColorStop(1, `hsla(${ch}, ${cs}%, ${cl}%, 0)`);
        ctx.beginPath();
        ctx.arc(sc.cx, sc.cy, outerR, 0, Math.PI * 2);
        ctx.fillStyle = outer;
        ctx.fill();

        // Mid glow
        const midR = baseR * 1.0 * breathe;
        const mid = ctx.createRadialGradient(sc.cx, sc.cy, 0, sc.cx, sc.cy, midR);
        const midAlpha = 0.09 * scBrightness;
        mid.addColorStop(0, `hsla(${ch}, ${Math.min(cs + 10, 95)}%, ${Math.min(cl + 8, 80)}%, ${Math.min(midAlpha, 0.2)})`);
        mid.addColorStop(0.5, `hsla(${ch}, ${cs}%, ${cl}%, ${Math.min(midAlpha * 0.4, 0.08)})`);
        mid.addColorStop(1, `hsla(${ch}, ${cs}%, ${cl}%, 0)`);
        ctx.beginPath();
        ctx.arc(sc.cx, sc.cy, midR, 0, Math.PI * 2);
        ctx.fillStyle = mid;
        ctx.fill();

        // Particle dots
        const dotAlphaScale = scBrightness * 0.6;
        for (const d of dots) {
          if (d.isBCG) {
            const twinkle = 0.8 + 0.2 * Math.sin(time * d.speed + d.phase);
            const alpha = Math.min(d.baseBrightness * twinkle * dotAlphaScale, 1);
            if (alpha < 0.03) continue;
            const glowR = d.glowSize * breathe;
            ctx.globalAlpha = Math.min(alpha * 0.35, 0.5);
            ctx.beginPath();
            ctx.arc(d.x, d.y, glowR, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${d.h}, ${d.s}%, ${Math.min(d.l + 20, 88)}%, 0.4)`;
            ctx.fill();
            ctx.globalAlpha = 1;
            ctx.beginPath();
            ctx.arc(d.x, d.y, d.size * 0.7, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${d.h}, ${Math.max(d.s - 15, 20)}%, ${Math.min(d.l + 30, 96)}%, ${Math.min(alpha, 1)})`;
            ctx.fill();
          } else {
            const twinkle = 0.65 + 0.35 * Math.sin(time * d.speed + d.phase);
            const alpha = Math.min(d.baseBrightness * twinkle * dotAlphaScale, 1);
            if (alpha < 0.03) continue;
            ctx.beginPath();
            ctx.arc(d.x, d.y, d.size * breathe, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${d.h}, ${Math.max(d.s - 10, 25)}%, ${Math.min(d.l + 20, 90)}%, ${alpha})`;
            ctx.fill();
          }
        }

        ctx.restore();
      }
    }

    // --- Neuron filament mesh (supercluster LoD) ---
    // Dense web of fine lines radiating from bright cluster nodes.
    if (neuronFils && hasSuperClusters && lod.superclusterFactor > 0) {
      const nfReveal = bigBangActive ? clamp01((bigBangT - 0.35) / 0.5) : 1;
      const nfBase = lod.superclusterFactor * nfReveal;

      // Draw all fine lines as quadratic curves
      ctx.lineWidth = 0.8;
      ctx.lineCap = 'round';
      for (const ln of neuronFils.lines) {
        const a = Math.min(ln.alpha * nfBase, 0.4);
        if (a < 0.015) continue;
        ctx.beginPath();
        ctx.moveTo(ln.x1, ln.y1);
        ctx.quadraticCurveTo(ln.cx, ln.cy, ln.x2, ln.y2);
        ctx.strokeStyle = `hsla(${ln.h}, ${Math.max(ln.s - 10, 18)}%, ${Math.min(ln.l + 20, 85)}%, ${a})`;
        ctx.stroke();
      }
      ctx.lineCap = 'butt';

      // Bright glow at cluster node intersections
      for (const nd of neuronFils.nodes) {
        const glowAlpha = Math.min(0.4 * nfBase, 0.5);
        if (glowAlpha < 0.03) continue;

        // Outer halo
        const grad = ctx.createRadialGradient(nd.x, nd.y, 0, nd.x, nd.y, nd.size * 4);
        grad.addColorStop(0, `hsla(${nd.h}, ${nd.s}%, ${Math.min(nd.l + 25, 92)}%, ${glowAlpha * 0.5})`);
        grad.addColorStop(0.3, `hsla(${nd.h}, ${Math.max(nd.s - 10, 20)}%, ${Math.min(nd.l + 15, 82)}%, ${glowAlpha * 0.15})`);
        grad.addColorStop(1, `hsla(${nd.h}, ${nd.s}%, ${nd.l}%, 0)`);
        ctx.beginPath();
        ctx.arc(nd.x, nd.y, nd.size * 4, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Bright core
        ctx.beginPath();
        ctx.arc(nd.x, nd.y, nd.size * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${nd.h}, ${Math.min(nd.s + 5, 90)}%, ${Math.min(nd.l + 30, 95)}%, ${glowAlpha * 0.7})`;
        ctx.fill();
      }
    }

    // --- Supercluster genre tags (visible at extreme zoom-out) ---
    if (hasSuperClusters && lod.superclusterFactor > 0 && superclusterMetas) {
      // Fade in slowly at the tail end of the big bang animation
      const scTagReveal = bigBangActive ? clamp01((bigBangT - 0.7) / 0.3) : 1;
      const scTagAlpha = lod.superclusterFactor * 0.35 * scTagReveal;
      const scFontSize = 12 / scale;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const sc of superclusterMetas) {
        if (!sc.genreTag) continue;
        if (!isVisible(sc.cx, sc.cy, sc.visualRadius, transform, w, h)) continue;

        const { h: ch, s: cs, l: cl } = sc.color;
        const fontSize = Math.round(scFontSize);
        ctx.font = `300 ${fontSize}px Inter, sans-serif`;
        ctx.globalAlpha = scTagAlpha;
        ctx.fillStyle = `hsl(${ch}, ${Math.max(cs - 10, 20)}%, ${Math.min(cl + 20, 85)}%)`;
        ctx.fillText(sc.genreTag, sc.cx, sc.cy + sc.visualRadius * 0.45);
      }
      ctx.globalAlpha = 1;
    }

    // --- Cosmic filaments ---
    if (filaments && lod.hazeFactor > 0) {
      const filReveal = bigBangActive ? clamp01((bigBangT - 0.4) / 0.5) : 1;
      const filAlpha = lod.hazeFactor * (1 + lod.overviewBoost * 0.8) * filReveal;
      ctx.globalAlpha = Math.min(filAlpha, 1);
      for (const fil of filaments) {
        for (const d of fil.dots) {
          // Bright in center, fade near cluster edges
          const edgeFade = Math.max(0, 1 - Math.abs(d.t - 0.5) * 2.5);
          const twinkle = 0.5 + 0.5 * Math.sin(time * 0.001 + d.phase);
          const alpha = 0.4 * edgeFade * twinkle * (0.3 + fil.proximity * 0.7);
          if (alpha < 0.02) continue;
          ctx.beginPath();
          ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${d.h}, 30%, 72%, ${alpha})`;
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    // --- Cluster hazes ---
    if (hazes) {
      // Brightness increases at farther zoom-out for dramatic overview
      const brightnessBoost = (1 + lod.overviewBoost * 1.5) * bbHazeReveal;

      for (const { dots, cm, baseR } of hazes) {
        const hazeCx = bigBangActive ? worldCenterX + (cm.cx - worldCenterX) * bbExpand : cm.cx;
        const hazeCy = bigBangActive ? worldCenterY + (cm.cy - worldCenterY) * bbExpand : cm.cy;
        if (!isVisible(hazeCx, hazeCy, baseR * 2, transform, w, h)) continue;

        ctx.save();
        if (bigBangActive) ctx.translate(hazeCx - cm.cx, hazeCy - cm.cy);

        const breathe = 1 + 0.025 * Math.sin(time * 0.0004 + cm.cx * 0.008);
        const { h: ch, s: cs, l: cl } = cm.color;

        // --- Multi-layer nebula gradients (visible when hazeFactor > 0) ---
        if (lod.hazeFactor > 0) {
          // Spread clusters get moderately less haze — keep cores warm
          const spreadDampen = clamp01(180 / Math.max(baseR, 50));
          ctx.globalAlpha = lod.hazeFactor * (0.5 + spreadDampen * 0.5);

          // Vibrant outer halo — saturated, large
          const outerR = baseR * 1.6 * breathe;
          const outerS = Math.min(cs + 20, 95); // boost saturation
          const outer = ctx.createRadialGradient(cm.cx, cm.cy, 0, cm.cx, cm.cy, outerR);
          const outerAlpha = 0.08 * brightnessBoost;
          outer.addColorStop(0, `hsla(${ch}, ${outerS}%, ${cl}%, ${Math.min(outerAlpha, 0.22)})`);
          outer.addColorStop(0.4, `hsla(${ch}, ${Math.max(cs, 40)}%, ${cl}%, ${Math.min(outerAlpha * 0.45, 0.1)})`);
          outer.addColorStop(1, `hsla(${ch}, ${cs}%, ${cl}%, 0)`);
          ctx.beginPath();
          ctx.arc(cm.cx, cm.cy, outerR, 0, Math.PI * 2);
          ctx.fillStyle = outer;
          ctx.fill();

          // Mid nebula — vivid color
          const midR = baseR * 1.0 * breathe;
          const midS = Math.min(cs + 25, 95);
          const mid = ctx.createRadialGradient(cm.cx, cm.cy, 0, cm.cx, cm.cy, midR);
          const midAlpha = 0.12 * brightnessBoost;
          mid.addColorStop(0, `hsla(${ch}, ${midS}%, ${Math.min(cl + 12, 80)}%, ${Math.min(midAlpha, 0.3)})`);
          mid.addColorStop(0.5, `hsla(${ch}, ${Math.max(cs + 5, 40)}%, ${cl}%, ${Math.min(midAlpha * 0.38, 0.1)})`);
          mid.addColorStop(1, `hsla(${ch}, ${cs}%, ${cl}%, 0)`);
          ctx.beginPath();
          ctx.arc(cm.cx, cm.cy, midR, 0, Math.PI * 2);
          ctx.fillStyle = mid;
          ctx.fill();

          // Core glow — warm bright center
          const coreR = baseR * 0.35 * breathe;
          const warmH = ch + (ch < 60 || ch > 300 ? 0 : -10); // shift slightly warm
          const core = ctx.createRadialGradient(cm.cx, cm.cy, 0, cm.cx, cm.cy, coreR);
          const coreAlpha = 0.12 * brightnessBoost;
          core.addColorStop(0, `hsla(${warmH}, ${Math.min(cs + 20, 95)}%, ${Math.min(cl + 18, 88)}%, ${Math.min(coreAlpha, 0.28)})`);
          core.addColorStop(0.4, `hsla(${ch}, ${Math.min(cs + 8, 85)}%, ${Math.min(cl + 10, 80)}%, ${Math.min(coreAlpha * 0.4, 0.12)})`);
          core.addColorStop(1, `hsla(${ch}, ${cs}%, ${cl}%, 0)`);
          ctx.beginPath();
          ctx.arc(cm.cx, cm.cy, coreR, 0, Math.PI * 2);
          ctx.fillStyle = core;
          ctx.fill();

          ctx.globalAlpha = 1;
        }

        // --- Nucleus point — subtle center glow ---
        if (lod.hazeFactor > 0) {
          const nucleusPulse = 0.9 + 0.1 * Math.sin(time * 0.0006 + cm.cy * 0.01);
          const nucleusR = Math.max(3, baseR * 0.04) * nucleusPulse;
          const nucleusAlpha = (0.3 + lod.overviewBoost * 0.2) * lod.hazeFactor * bbHazeReveal;

          const nGlow = ctx.createRadialGradient(cm.cx, cm.cy, 0, cm.cx, cm.cy, nucleusR * 3);
          nGlow.addColorStop(0, `hsla(${ch}, ${Math.min(cs + 10, 95)}%, ${Math.min(cl + 20, 90)}%, ${nucleusAlpha * 0.5})`);
          nGlow.addColorStop(0.4, `hsla(${ch}, ${cs}%, ${Math.min(cl + 10, 80)}%, ${nucleusAlpha * 0.15})`);
          nGlow.addColorStop(1, `hsla(${ch}, ${cs}%, ${cl}%, 0)`);
          ctx.beginPath();
          ctx.arc(cm.cx, cm.cy, nucleusR * 3, 0, Math.PI * 2);
          ctx.fillStyle = nGlow;
          ctx.fill();

          // Soft center point
          ctx.beginPath();
          ctx.arc(cm.cx, cm.cy, nucleusR, 0, Math.PI * 2);
          ctx.fillStyle = `hsla(${ch}, 30%, 88%, ${nucleusAlpha * 0.7})`;
          ctx.fill();
        }

        // --- Particle dots ---
        const dotAlphaScale = (0.5 + lod.hazeFactor * 0.5) * brightnessBoost;
        for (const d of dots) {
          if (d.isBCG) {
            const twinkle = 0.82 + 0.18 * Math.sin(time * d.speed + d.phase);
            const alpha = Math.min(d.baseBrightness * twinkle * dotAlphaScale, 1);
            if (alpha < 0.03) continue;

            // Glow halo
            const glowR = d.glowSize * breathe;
            ctx.globalAlpha = Math.min(alpha * 0.4, 0.6);
            ctx.beginPath();
            ctx.arc(d.x, d.y, glowR, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${d.h}, ${d.s}%, ${Math.min(d.l + 25, 90)}%, 0.5)`;
            ctx.fill();
            ctx.globalAlpha = 1;

            // Diffraction spikes on bright BCGs
            if (alpha > 0.2 && d.size > 2.5) {
              const spikeLen = d.glowSize * 1.2;
              const spikeAng = d.phase;
              ctx.lineWidth = 0.5;
              for (let s = 0; s < 4; s++) {
                const ang = spikeAng + s * Math.PI / 2;
                const ex = d.x + Math.cos(ang) * spikeLen;
                const ey = d.y + Math.sin(ang) * spikeLen;
                const grad = ctx.createLinearGradient(d.x, d.y, ex, ey);
                grad.addColorStop(0, `hsla(${d.h}, ${Math.max(d.s - 20, 15)}%, ${Math.min(d.l + 30, 95)}%, ${alpha * 0.45})`);
                grad.addColorStop(0.5, `hsla(${d.h}, ${Math.max(d.s - 20, 15)}%, ${Math.min(d.l + 20, 85)}%, ${alpha * 0.1})`);
                grad.addColorStop(1, `hsla(${d.h}, ${d.s}%, ${d.l}%, 0)`);
                ctx.beginPath();
                ctx.moveTo(d.x, d.y);
                ctx.lineTo(ex, ey);
                ctx.strokeStyle = grad;
                ctx.stroke();
              }
            }

            // Bright core
            ctx.beginPath();
            ctx.arc(d.x, d.y, d.size * 0.6, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${d.h}, ${Math.max(d.s - 15, 20)}%, ${Math.min(d.l + 35, 98)}%, ${Math.min(alpha, 1)})`;
            ctx.fill();
          } else {
            const twinkle = 0.7 + 0.3 * Math.sin(time * d.speed + d.phase);
            const alpha = Math.min(d.baseBrightness * twinkle * dotAlphaScale, 1);
            if (alpha < 0.03) continue;

            ctx.beginPath();
            ctx.arc(d.x, d.y, d.size * breathe, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${d.h}, ${Math.max(d.s - 10, 25)}%, ${Math.min(d.l + 25, 92)}%, ${alpha})`;
            ctx.fill();
          }
        }

        ctx.restore();
      }
    }

    // --- Genre tags (subtle, visible at cluster-overview zoom) ---
    if (lod.genreTagFactor > 0 && clusterMetas) {
      const tagAlpha = lod.genreTagFactor * 0.4;
      const baseFontSize = 10 / scale; // constant screen-space size
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const cm of clusterMetas) {
        if (!cm.genreTag) continue;
        const effX = bigBangActive ? worldCenterX + (cm.cx - worldCenterX) * bbExpand : cm.cx;
        const effY = bigBangActive ? worldCenterY + (cm.cy - worldCenterY) * bbExpand : cm.cy;
        if (!isVisible(effX, effY, cm.visualRadius, transform, w, h)) continue;

        const { h: ch, s: cs, l: cl } = cm.color;
        const fontSize = Math.round(baseFontSize * 0.9);
        ctx.font = `300 ${fontSize}px Inter, sans-serif`;
        ctx.globalAlpha = tagAlpha;
        ctx.fillStyle = `hsl(${ch}, ${Math.max(cs - 10, 20)}%, ${Math.min(cl + 20, 85)}%)`;
        ctx.fillText(cm.genreTag, effX, effY + cm.visualRadius * 0.55);
      }
      ctx.globalAlpha = 1;
    }

    // --- Inter-cluster bridge links (visible from LOD 2) ---
    if (lod.nodeFactor > 0 && allLinks) {
      const bridgeAlpha = lod.nodeFactor * 0.6;
      ctx.globalAlpha = bridgeAlpha;
      ctx.setLineDash([3, 5]);
      for (const link of allLinks) {
        if (!link.isBridgeLink) continue;
        const source = link.source;
        const target = link.target;
        if (!source || !target || source.x == null || target.x == null) continue;

        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        ctx.strokeStyle = 'rgba(140, 160, 200, 0.15)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // --- Chain links (purple dashed, visible from LOD 2) ---
    if (lod.nodeFactor > 0 && allLinks) {
      const chainAlpha = lod.nodeFactor * 0.7;
      ctx.globalAlpha = chainAlpha;
      for (const link of allLinks) {
        if (!link.isChainLink) continue;
        const source = link.source;
        const target = link.target;
        if (!source || !target || source.x == null || target.x == null) continue;

        const isHighlighted = source === hoveredNode || target === hoveredNode ||
                              source === selectedNode || target === selectedNode;

        // Animated dash offset for flowing effect
        const dashOffset = (time * 0.015) % 16;

        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        ctx.setLineDash([4, 4]);
        ctx.lineDashOffset = -dashOffset;
        ctx.strokeStyle = isHighlighted
          ? 'rgba(200, 160, 255, 0.5)'
          : 'rgba(200, 160, 255, 0.2)';
        ctx.lineWidth = isHighlighted ? 1.5 : 1;
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.lineDashOffset = 0;
      ctx.globalAlpha = 1;
    }

    // --- Links (LOD 3 only) ---
    if (lod.detailFactor > 0 && allLinks) {
      ctx.globalAlpha = lod.detailFactor;
      for (const link of allLinks) {
        if (link.isBridgeLink || link.isChainLink) continue;
        const source = link.source;
        const target = link.target;
        if (!source || !target || source.x == null || target.x == null) continue;

        const isHighlighted = source === hoveredNode || target === hoveredNode ||
                              source === selectedNode || target === selectedNode;

        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);

        if (isHighlighted) {
          ctx.setLineDash([]);
          ctx.strokeStyle = GALAXY_COLORS.linkHighlight;
          ctx.lineWidth = 1.2;
        } else {
          ctx.setLineDash([]);
          ctx.strokeStyle = `rgba(80, 100, 140, ${link.opacity || 0.06})`;
          ctx.lineWidth = 0.5;
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
      ctx.globalAlpha = 1;
    }

    // --- Nodes (LOD 2+) ---
    if (lod.nodeFactor > 0 && allNodes) {
      ctx.globalAlpha = lod.nodeFactor;
      for (const node of allNodes) {
        if (node.x == null || node.y == null) continue;
        if (!isVisible(node.x, node.y, (node.radius || 5) * 3, transform, w, h)) continue;

        const isActive = node === hoveredNode || node === selectedNode;

        if (node.type === 'seed') {
          drawSeedNode(ctx, node, isActive);
        } else {
          drawRecNode(ctx, node, isActive);
        }
      }

      // Gold/blue glow on discovered/favorited nodes at LOD 2 (visible before full detail rings)
      if (lod.detailFactor < 1) {
        const glowAlpha = lod.nodeFactor * (1 - lod.detailFactor);
        if (glowAlpha > 0) {
          ctx.globalAlpha = glowAlpha;

          if (discoveredNames && discoveredNames.size > 0) {
            for (const node of allNodes) {
              if (node.x == null || !discoveredNames.has(node.name)) continue;
              if (!isVisible(node.x, node.y, (node.radius || 5) * 4, transform, w, h)) continue;
              const r = node.radius || 5;
              const glowR = r * 3.5;
              const glow = ctx.createRadialGradient(node.x, node.y, r * 0.5, node.x, node.y, glowR);
              glow.addColorStop(0, 'rgba(255, 200, 50, 0.5)');
              glow.addColorStop(0.4, 'rgba(255, 180, 0, 0.15)');
              glow.addColorStop(1, 'rgba(255, 150, 0, 0)');
              ctx.beginPath();
              ctx.arc(node.x, node.y, glowR, 0, Math.PI * 2);
              ctx.fillStyle = glow;
              ctx.fill();
            }
          }

          if (favoriteNames && favoriteNames.size > 0) {
            for (const node of allNodes) {
              if (node.x == null || !favoriteNames.has(node.name)) continue;
              if (!isVisible(node.x, node.y, (node.radius || 5) * 4, transform, w, h)) continue;
              const r = node.radius || 5;
              const glowR = r * 3.5;
              const glow = ctx.createRadialGradient(node.x, node.y, r * 0.5, node.x, node.y, glowR);
              glow.addColorStop(0, 'rgba(59, 130, 246, 0.5)');
              glow.addColorStop(0.4, 'rgba(30, 64, 175, 0.15)');
              glow.addColorStop(1, 'rgba(30, 58, 138, 0)');
              ctx.beginPath();
              ctx.arc(node.x, node.y, glowR, 0, Math.PI * 2);
              ctx.fillStyle = glow;
              ctx.fill();
            }
          }
        }
      }

      ctx.globalAlpha = 1;
    }

    // --- Indicators (LOD 3 only) — single pass over allNodes ---
    if (lod.detailFactor > 0 && allNodes) {
      ctx.globalAlpha = lod.detailFactor;

      const hasFavs = favoriteNames && favoriteNames.size > 0;
      const hasDislikes = dislikeNames && dislikeNames.size > 0;
      const hasDiscovered = discoveredNames && discoveredNames.size > 0;
      const angle = time * 0.001;

      for (const node of allNodes) {
        if (node.x == null) continue;

        // Discovered ring (gold swirl + sparkles)
        if (hasDiscovered && discoveredNames.has(node.name)) {
          const { x, y, radius } = node;

          // 1. Pulsing outer glow
          const pulse = 0.7 + 0.3 * Math.sin(time * 0.002 + x * 0.01);
          const glowRadius = (radius + 8) * pulse + radius;
          const glow = ctx.createRadialGradient(x, y, radius, x, y, glowRadius);
          glow.addColorStop(0, `rgba(255, 200, 50, ${0.25 * pulse})`);
          glow.addColorStop(0.5, `rgba(255, 170, 0, ${0.1 * pulse})`);
          glow.addColorStop(1, 'rgba(255, 150, 0, 0)');
          ctx.beginPath();
          ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
          ctx.fillStyle = glow;
          ctx.fill();

          // 2. Rotating gradient ring (swirl)
          const ringR = radius + 3;
          const gx = x + Math.cos(angle) * ringR;
          const gy = y + Math.sin(angle) * ringR;
          const gx2 = x + Math.cos(angle + Math.PI) * ringR;
          const gy2 = y + Math.sin(angle + Math.PI) * ringR;
          const ringGrad = ctx.createLinearGradient(gx, gy, gx2, gy2);
          ringGrad.addColorStop(0, 'rgba(255, 230, 100, 1)');
          ringGrad.addColorStop(0.25, 'rgba(255, 190, 0, 0.95)');
          ringGrad.addColorStop(0.5, 'rgba(255, 140, 0, 0.9)');
          ringGrad.addColorStop(0.75, 'rgba(255, 200, 50, 0.95)');
          ringGrad.addColorStop(1, 'rgba(255, 230, 100, 1)');
          ctx.beginPath();
          ctx.arc(x, y, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = ringGrad;
          ctx.lineWidth = 2.5;
          ctx.stroke();

          // 3. Orbiting sparkle particles
          for (let i = 0; i < 4; i++) {
            const sparkleAngle = angle * 1.5 + (Math.PI * 2 * i) / 4;
            const sparkleR = ringR + 1;
            const sx = x + Math.cos(sparkleAngle) * sparkleR;
            const sy = y + Math.sin(sparkleAngle) * sparkleR;
            const sparkleAlpha = 0.5 + 0.5 * Math.sin(time * 0.004 + i * 1.5);
            const sparkleSize = 1.2 + 0.6 * sparkleAlpha;

            ctx.save();
            ctx.translate(sx, sy);
            ctx.rotate(sparkleAngle);
            ctx.beginPath();
            ctx.moveTo(0, -sparkleSize * 2);
            ctx.lineTo(sparkleSize * 0.4, 0);
            ctx.lineTo(0, sparkleSize * 2);
            ctx.lineTo(-sparkleSize * 0.4, 0);
            ctx.closePath();
            ctx.fillStyle = `rgba(255, 240, 180, ${sparkleAlpha * 0.9})`;
            ctx.fill();
            ctx.beginPath();
            ctx.moveTo(-sparkleSize * 2, 0);
            ctx.lineTo(0, sparkleSize * 0.4);
            ctx.lineTo(sparkleSize * 2, 0);
            ctx.lineTo(0, -sparkleSize * 0.4);
            ctx.closePath();
            ctx.fillStyle = `rgba(255, 240, 180, ${sparkleAlpha * 0.7})`;
            ctx.fill();
            ctx.restore();
          }
          continue; // discovered takes priority, skip other indicators
        }

        // Favorite ring (blue gradient)
        if (hasFavs && favoriteNames.has(node.name)) {
          const ringR = node.radius + 3;
          const grad = ctx.createLinearGradient(node.x - ringR, node.y - ringR, node.x + ringR, node.y + ringR);
          grad.addColorStop(0, 'rgba(30, 64, 175, 0.95)');
          grad.addColorStop(0.5, 'rgba(59, 130, 246, 0.95)');
          grad.addColorStop(1, 'rgba(30, 58, 138, 0.95)');
          ctx.beginPath();
          ctx.arc(node.x, node.y, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = grad;
          ctx.lineWidth = 2.5;
          ctx.stroke();
          continue;
        }

        // Dislike ring (red dashed)
        if (hasDislikes && dislikeNames.has(node.name)) {
          const ringR = node.radius + 3;
          ctx.beginPath();
          ctx.arc(node.x, node.y, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(239, 68, 68, 0.5)';
          ctx.lineWidth = 2;
          ctx.setLineDash([4, 4]);
          ctx.stroke();
          ctx.setLineDash([]);
          continue;
        }

        // Hidden gem indicator (teal ring)
        if (node.isHiddenGem && !node.isChainLink) {
          const ringR = node.radius + 3.5;
          const sparkle = 0.5 + 0.2 * Math.sin(time * 0.002 + node.x * 0.05);
          const grad = ctx.createLinearGradient(node.x - ringR, node.y - ringR, node.x + ringR, node.y + ringR);
          grad.addColorStop(0, `rgba(100, 220, 200, ${0.7 * sparkle})`);
          grad.addColorStop(0.5, `rgba(120, 235, 215, ${0.8 * sparkle})`);
          grad.addColorStop(1, `rgba(80, 200, 180, ${0.7 * sparkle})`);
          ctx.beginPath();
          ctx.arc(node.x, node.y, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = grad;
          ctx.lineWidth = 2;
          ctx.stroke();
          continue;
        }

        // Chain link indicator (purple ring)
        if (node.isChainLink) {
          const ringR = node.radius + 3.5;
          const pulse = 0.6 + 0.15 * Math.sin(time * 0.0015 + node.y * 0.04);
          const grad = ctx.createLinearGradient(node.x - ringR, node.y - ringR, node.x + ringR, node.y + ringR);
          grad.addColorStop(0, `rgba(200, 160, 255, ${0.7 * pulse})`);
          grad.addColorStop(0.5, `rgba(220, 180, 255, ${0.8 * pulse})`);
          grad.addColorStop(1, `rgba(180, 140, 235, ${0.7 * pulse})`);
          ctx.beginPath();
          ctx.arc(node.x, node.y, ringR, 0, Math.PI * 2);
          ctx.strokeStyle = grad;
          ctx.lineWidth = 2.5;
          ctx.stroke();
        }
      }

      ctx.globalAlpha = 1;
    }

    // --- Node labels (LOD 2: selective, LOD 3: hovered/selected) ---
    if (lod.labelFactor > 0 && allNodes) {
      if (lod.detailFactor < 1) {
        ctx.globalAlpha = lod.labelFactor * (1 - lod.detailFactor);
        ensureLabelMap(allNodes, clusterMetas);
        for (const cm of clusterMetas) {
          for (const name of cm.labelNames) {
            const node = labelMap.get(name + '|' + cm.index);
            if (node && node.x != null) {
              drawNodeLabel(ctx, node);
            }
          }
        }
        ctx.globalAlpha = 1;
      }

      if (lod.detailFactor > 0) {
        ctx.globalAlpha = lod.detailFactor;
        if (hoveredNode && hoveredNode !== selectedNode && hoveredNode.x != null) {
          drawNodeLabel(ctx, hoveredNode);
        }
        if (selectedNode && selectedNode.x != null) {
          drawNodeLabel(ctx, selectedNode);
        }
        ctx.globalAlpha = 1;
      }
    }

    // Always draw the selected node label so explore mode shows the artist name
    if (selectedNode && selectedNode.x != null && !(lod.labelFactor > 0 && lod.detailFactor > 0)) {
      drawNodeLabel(ctx, selectedNode);
    }

    ctx.restore();
    ctx.restore();

    frameId = requestAnimationFrame(render);
  }

  return {
    start() {
      if (frameId) return;
      lastFrameTime = performance.now();
      frameId = requestAnimationFrame(render);
    },
    stop() {
      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    },
  };
}

// ─── Node drawing ──────────────────────────────────────────────────────

function drawSeedNode(ctx, node, isActive) {
  const { x, y, radius } = node;

  const glowRadius = radius * 3;
  const glow = ctx.createRadialGradient(x, y, 0, x, y, glowRadius);
  glow.addColorStop(0, 'rgba(255, 215, 0, 0.6)');
  glow.addColorStop(0.3, 'rgba(255, 215, 0, 0.2)');
  glow.addColorStop(0.7, 'rgba(255, 215, 0, 0.05)');
  glow.addColorStop(1, 'rgba(255, 215, 0, 0)');
  ctx.beginPath();
  ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
  ctx.fillStyle = glow;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = '#FFD700';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y, radius * 0.5, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFBE6';
  ctx.fill();

  if (isActive) {
    ctx.beginPath();
    ctx.arc(x, y, radius + 5, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

function drawRecNode(ctx, node, isActive) {
  const { x, y, radius, color, glowColor, brightness } = node;

  const glowRadius = radius * 2.5;
  const glow = ctx.createRadialGradient(x, y, 0, x, y, glowRadius);
  glow.addColorStop(0, glowColor || 'rgba(100, 150, 255, 0.2)');
  glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.beginPath();
  ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
  ctx.fillStyle = glow;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color || 'rgba(100, 150, 255, 0.8)';
  ctx.fill();

  if ((brightness || 0) > 0.7) {
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.4, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255, 255, 255, ${(brightness - 0.7) * 2})`;
    ctx.fill();
  }

  if (isActive) {
    ctx.beginPath();
    ctx.arc(x, y, radius + 4, 0, Math.PI * 2);
    ctx.strokeStyle = GALAXY_COLORS.hoverRing;
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
}

function drawNodeLabel(ctx, node) {
  if (!node || node.x == null) return;

  const label = node.name;
  ctx.font = "11px Inter, sans-serif";
  const metrics = ctx.measureText(label);
  const padding = 6;
  const labelW = metrics.width + padding * 2;
  const labelH = 20;
  const labelX = node.x - labelW / 2;
  const labelY = node.y - node.radius - labelH - 6;

  ctx.fillStyle = 'rgba(10, 10, 30, 0.85)';
  ctx.beginPath();
  roundRect(ctx, labelX, labelY, labelW, labelH, 4);
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.fillStyle = '#e8e8f0';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, node.x, labelY + labelH / 2);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
}
